import React, { useEffect, useId, useRef, useState } from 'react';
import { angleOf } from './engine/polar';
import { fmtMin } from './engine/vreme';
import { activityLabel } from '../../utils/activities';
import { izvorInfo } from '../common/IzvorAktivnosti';

const ACT_VAR = { driving: 'var(--ui-act-driving)', availability: 'var(--ui-act-availability)', work: 'var(--ui-act-work)', unknown: 'var(--ui-text-subtle)' };
const SATI = Array.from({ length: 24 }, (_, h) => h);
const PRAG_MIN = 5; // hvatanje granice aktivnosti: ± minuta
const LUPA_X = 3.5; // uvećanje lupe (desni klik na listić)

/** luk kružnice od minuta a do minuta b (u smeru kazaljke), skala uspravna (rot 0) */
const arc = (cx, cy, r, a, b) => {
    const span = ((b - a) + 1440) % 1440 || 1440;
    const t1 = angleOf(a, 0), t2 = angleOf(a + span - 0.01, 0);
    const p = (t) => `${cx + r * Math.cos(t)} ${cy + r * Math.sin(t)}`;
    return `M ${p(t1)} A ${r} ${r} 0 ${span > 720 ? 1 : 0} 1 ${p(t2)}`;
};
const IZVOR_OPIS = (s) => (s.izvor === 'LISTIC' && s.listic ? `listić ${s.listic}` : izvorInfo(s.izvor).label);
const kruzno = (a, b) => { const d = Math.abs(a - b) % 1440; return Math.min(d, 1440 - d); };

/* Listić u UTC: slika se okreće za prepoznatu rotaciju i za pomeraj Beograda (pomak, minuti: leti 120, zimi 60), pa
   spoljna skala (0–24, gore 6, desno 12, levo 24) pokazuje UTC — odštampano 6 sa letnjeg listića stoji na 4 UTC.
   Svi minuti (aktivnosti, segmenti, ulaganje) su već u prikaznom (UTC) vremenu. Blede ose kroz centar,
   obojeni lukovi aktivnosti, isprekidano = listić nije bio uložen, linija = početak aktivnosti (tahograf počinje da beleži).
   Izmena (kao kupe u pojedinačnoj obradi): vuče se granica aktivnosti; klik na luk aktivnosti je bira, klik pored skida izbor (tip se menja alatom,
   Delete briše); sa izabranim alatom bez izbora — klik i vučenje crta novu aktivnost. */
export default function DiscView({ src, width, height, result, segments, aktivnosti, ulaganje, srednja, pomak = 0, utc = false,
    onAktivnosti, alat, onAlatGotov, izabran, onIzabran, onUlaganje, postojece = [] }) {
    const clipId = useId().replace(/:/g, '');
    const svgRef = useRef(null);
    const [prikaz, setPrikaz] = useState(null); // aktivnosti tokom vučenja
    const vuce = useRef(null); // { i, kraj: 'start'|'end' }
    const [kursor, setKursor] = useState('');
    const [ulPrikaz, setUlPrikaz] = useState(null); // početak aktivnosti tokom vučenja
    const vuceUl = useRef(false);
    const pomerio = useRef(false); // posle vučenja klik ne menja izbor
    useEffect(() => { setPrikaz(null); vuce.current = null; }, [aktivnosti]);
    /* lupa (desni klik pali / gasi): uvećan deo listića ispod miša u uglu — za sitne aktivnosti */
    const [lupa, setLupa] = useState(null); // null = ugašena, inače { x, y } miša u koordinatama listića ({ x: null } = miš van listića)
    useEffect(() => { setLupa(null); }, [src]); // drugi listić gasi lupu
    const lupaUkljucena = !!lupa;
    useEffect(() => {
        if (!lupaUkljucena) return undefined;
        /* bilo koji klik van listića (dugmići alata, polja, spisak) gasi lupu; Esc takođe */
        const van = (e) => { if (svgRef.current && !svgRef.current.contains(e.target)) setLupa(null); };
        const esc = (e) => { if (e.key === 'Escape') setLupa(null); };
        document.addEventListener('mousedown', van, true);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', van, true); document.removeEventListener('keydown', esc); };
    }, [lupaUkljucena]);
    if (!src || !result) return null;
    const { disc, rotMin } = result;
    const { cx, cy } = disc;
    const R = disc.r;
    /* slika se okreće oko centra rotacije (zapis), a papir je isečen ekscentrično: ivica papira (isecanje slike,
       obod) je krug oko centra ivice, okrenutog istim uglom kao slika */
    const ugao = -(rotMin + pomak) / 4;
    const ex = (disc.ivicaCx ?? cx) - cx, ey = (disc.ivicaCy ?? cy) - cy, t = (ugao * Math.PI) / 180;
    const pcx = cx + ex * Math.cos(t) - ey * Math.sin(t), pcy = cy + ex * Math.sin(t) + ey * Math.cos(t);
    /* listić je često malo veći od skenirane površine (skener odseče par px papira): prikaz se iseca krugom koji
       ceo stane u sliku — bez ravnih ivica i popunjenih "srpova"; najviše do 98.5% R (vlasnik: ne seći listić; jače odsečen listić se dopunjuje bojom papira) */
    const icx = cx + ex, icy = cy + ey;
    /* krug iz štampe (sken bez podloge, ivica odsečena): R motora nije ivica papira — prikazuje se do 1.06 R da se papir ne iseca */
    /* rIvica: R motora je iz štampe, a ivica papira je izmerena posebno (180-24) */
    const Rivica = disc.rIvica || R;
    const Rp = disc.izStampe ? 1.06 * R : Math.max(0.985 * Rivica, Math.min(Rivica, icx - 1, icy - 1, width - 2 - icx, height - 2 - icy));
    const M = 1.17 * R + Math.hypot(ex, ey); // okvir: listić + prsten UTC skale
    const rBand = 0.535 * R, wBand = 0.06 * R;
    const u = angleOf(ulPrikaz ?? ulaganje, 0);
    const tipBoja = (t) => ACT_VAR[t === 'availability' ? srednja : t] || ACT_VAR.unknown;
    const pt = (r, minute) => [cx + r * Math.cos(angleOf(minute, 0)), cy + r * Math.sin(angleOf(minute, 0))];
    const akt = prikaz || aktivnosti || [];
    const izmena = !!onAktivnosti;
    /* početak aktivnosti se prevlači za spoljni deo zelene linije (iznad traka, da se ne meša sa granicama aktivnosti) */
    const naPocetku = (minut, r) => !!onUlaganje && r > 0.62 && r < 1.05 && kruzno(minut, ulaganje) <= PRAG_MIN;

    /** tačka miša u koordinatama listića (viewBox) */
    const svgTacka = (e) => {
        const svg = svgRef.current;
        const p = svg.createSVGPoint();
        p.x = e.clientX; p.y = e.clientY;
        return p.matrixTransform(svg.getScreenCTM().inverse());
    };
    /** lupa: tačka miša + vidljiv deo crteža (viewBox je kvadrat, a okvir širi — lupa ide u ugao OKVIRA, ne kvadrata) */
    const lupaNa = (e) => {
        const svg = svgRef.current, b = svg.getBoundingClientRect(), q = svgTacka(e);
        const a = svgTacka({ clientX: b.left, clientY: b.top }), z = svgTacka({ clientX: b.right, clientY: b.bottom });
        return { x: q.x, y: q.y, v: { x0: a.x, y0: a.y, x1: z.x, y1: z.y } };
    };
    /** tačka miša -> { minut, r (deo R) } u koordinatama listića */
    const tacka = (e) => {
        const q = svgTacka(e);
        const a = Math.atan2(q.y - cy, q.x - cx);
        return { minut: Math.round((((a + Math.PI) / (2 * Math.PI)) * 1440 + 1440) % 1440), r: Math.hypot(q.x - cx, q.y - cy) / R };
    };
    const granica = (m) => {
        let best = null;
        akt.forEach((a, i) => {
            ['start', 'end'].forEach((kraj) => {
                const d = kruzno(m, a[kraj] % 1440);
                if (d <= PRAG_MIN && (!best || d < best.d)) best = { i, kraj, d };
            });
        });
        return best;
    };
    const naAktivnosti = (m) => akt.findIndex((a) => m >= a.start && m < a.end);
    /** granica se vuče slobodno, ali staje na susednoj aktivnosti — ne prelazi preko druge aktivnosti (vlasnik 2026-11-04;
        ranije je vučena granica prekrivala susede) */
    /* aktivnosti koje se VEĆ preklapaju (prepoznate / nacrtane) ostaju — ograničeno je samo pomeranje: granica koja je već
       unutar druge aktivnosti sme samo nazad (smanjuje preklapanje), ne dublje preko nje */
    const opsegGranice = (i, kraj) => {
        const a = akt[i];
        const ostale = akt.filter((_, j) => j !== i);
        if (kraj === 'start') {
            if (ostale.some((b) => b.start < a.start && b.end > a.start)) return { lo: a.start, hi: a.end - 1 };
            const pre = ostale.filter((b) => b.end <= a.start).map((b) => b.end);
            return { lo: pre.length ? Math.max(...pre) : 0, hi: a.end - 1 };
        }
        if (ostale.some((b) => b.start < a.end && b.end > a.end)) return { lo: a.start + 1, hi: a.end };
        const posle = ostale.filter((b) => b.start >= a.end).map((b) => b.start);
        return { lo: a.start + 1, hi: posle.length ? Math.min(...posle) : 1440 };
    };

    const down = (e) => {
        if (e.button !== 0) return;
        /* fokus na listić: inače ostaje u polju (datum, vozač) i Delete/Esc se ne primenjuju na izabranu aktivnost */
        if (svgRef.current && document.activeElement !== svgRef.current) svgRef.current.focus({ preventScroll: true });
        pomerio.current = false;
        const { minut, r } = tacka(e);
        if (naPocetku(minut, r)) { e.preventDefault(); vuceUl.current = true; setUlPrikaz(minut); return; }
        if (!izmena) return;
        if (r < 0.2 || r > 1.05) return;
        e.preventDefault();
        if (alat && izabran == null) {
            const nove = [...akt, { start: minut, end: Math.min(1440, minut + 1), type: alat }];
            vuce.current = { i: nove.length - 1, kraj: 'end', nova: true };
            setPrikaz(nove);
            if (onAlatGotov) onAlatGotov();
            return;
        }
        const g = granica(minut);
        if (g) {
            /* dodirne aktivnosti (kraj jedne = početak druge): koja se skraćuje odlučuje smer prvog pomeranja */
            const m0 = akt[g.i][g.kraj] % 1440;
            const par = akt.findIndex((a, j) => j !== g.i && (g.kraj === 'end' ? a.start : a.end % 1440) === m0);
            vuce.current = { ...g, ...opsegGranice(g.i, g.kraj), m0, par: par >= 0 ? par : null };
            setPrikaz(akt.map((a) => ({ ...a })));
        }
    };
    const move = (e) => {
        if (lupa) setLupa(lupaNa(e));
        const { minut, r } = tacka(e);
        if (vuceUl.current) { pomerio.current = true; setUlPrikaz(minut); return; }
        if (!izmena) { setKursor(naPocetku(minut, r) ? 'granica' : ''); return; }
        if (!vuce.current) {
            setKursor(naPocetku(minut, r) ? 'granica' : alat && izabran == null ? 'crtanje' : (r > 0.2 && r < 1.05 && granica(minut) ? 'granica' : ''));
            return;
        }
        pomerio.current = true;
        const v = vuce.current;
        if (v.par != null && minut !== v.m0) {
            /* vuče se ka drugoj aktivnosti: pomera se njena granica (ona se skraćuje), ne preko nje */
            const kaDrugoj = v.kraj === 'end' ? kruzno(minut, v.m0) > 0 && ((minut - v.m0 + 1440) % 1440) < 720 : ((v.m0 - minut + 1440) % 1440) < 720;
            if (kaDrugoj) { const kraj2 = v.kraj === 'end' ? 'start' : 'end'; vuce.current = { i: v.par, kraj: kraj2, ...opsegGranice(v.par, kraj2) }; } else vuce.current = { ...v, par: null };
        }
        const { i, kraj, nova, lo, hi } = vuce.current;
        setPrikaz((p) => p.map((a, k) => {
            if (nova) {
                if (k !== i) return a;
                /* nova (nacrtana) aktivnost: kraj ne prelazi njen početak; kraj u 0 = ponoć (24:00) */
                const kr = minut === 0 && a.start > 0 ? 1440 : minut;
                return { ...a, end: Math.max(kr, a.start + 1) };
            }
            /* postojeća granica: samo između suseda (bez preklapanja); kraj u 0 = ponoć (24:00) */
            const m = kraj === 'end' && minut === 0 && p[i].start > 0 ? 1440 : minut;
            const x = Math.max(lo, Math.min(hi, m));
            if (k !== i) return a;
            return kraj === 'start' ? { ...a, start: x } : { ...a, end: x };
        }));
    };
    const up = () => {
        if (vuceUl.current) { vuceUl.current = false; if (ulPrikaz != null && ulPrikaz !== ulaganje) onUlaganje(ulPrikaz); setUlPrikaz(null); return; }
        if (!vuce.current) return;
        vuce.current = null;
        const p = (prikaz || []).filter((a) => a.end > a.start);
        onAktivnosti(p);
    };
    /* klik na luk aktivnosti (samo u traci gde je očitana) je odmah bira; klik bilo gde drugde skida izbor */
    const klik = (e) => {
        if (!izmena || !onIzabran || alat || pomerio.current) { pomerio.current = false; return; }
        const { minut, r } = tacka(e);
        const uTraci = Math.abs(r * R - rBand) <= wBand * 0.75;
        const i = uTraci ? naAktivnosti(minut) : -1;
        onIzabran(i >= 0 ? i : null);
    };


    const izabranA = izabran != null ? akt[izabran] : null;
    return (
        <svg ref={svgRef} className={`disc-view${izmena ? ' editable' : ''}${kursor ? ` k-${kursor}` : ''}`} viewBox={`${cx - M} ${cy - M} ${2 * M} ${2 * M}`} role="img" tabIndex={-1}
            aria-label={`Listić sa prepoznatim aktivnostima, početak aktivnosti u ${fmtMin(ulaganje)}`}
            onMouseDown={down} onMouseMove={move} onMouseUp={up} onClick={klik}
            onMouseLeave={() => { up(); if (lupa) setLupa({ x: null }); }}
            onContextMenu={(e) => { e.preventDefault(); if (lupa) setLupa(null); else setLupa(lupaNa(e)); }}>
            <defs>
                <clipPath id={`disc-clip-${clipId}`}><circle cx={pcx} cy={pcy} r={Rp} /></clipPath>
                {/* šrafura aktivnosti iz baze (domenski crtež na listiću, kao senka u šrafuri) */}
                <pattern id={`disc-sraf-${clipId}`} patternUnits="userSpaceOnUse" width={0.014 * R} height={0.014 * R} patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2={0.014 * R} stroke="#000" strokeOpacity={0.75} strokeWidth={0.005 * R} />
                </pattern>
            </defs>
            <g id={`disc-sadrzaj-${clipId}`}>
            <g clipPath={`url(#disc-clip-${clipId})`}>
                {/* deo listića koji je skener odsekao (listić veći od slike) popunjava se bojom papira (domenska boja sa slike) */}
                {disc.papir && <circle cx={pcx} cy={pcy} r={Rp} fill={`rgb(${disc.papir.join(',')})`} />}
                <g transform={`rotate(${ugao} ${cx} ${cy})`}>
                    <image href={src} x="0" y="0" width={width} height={height} preserveAspectRatio="none" />
                </g>
            </g>
            <circle cx={pcx} cy={pcy} r={Rp} className="disc-edge" style={{ strokeWidth: 0.004 * R }} />
            {/* blede ose */}
            <line x1={cx - M} y1={cy} x2={cx + M} y2={cy} className="disc-axis" style={{ strokeWidth: 0.003 * R }} />
            <line x1={cx} y1={cy - M} x2={cx} y2={cy + M} className="disc-axis" style={{ strokeWidth: 0.003 * R }} />
            {segments.filter((s) => !s.type).map((s) => (
                <path key={`g${s.start}`} d={arc(cx, cy, 0.6 * R, s.start, s.end)} className="disc-gap" style={{ strokeWidth: 0.012 * R }} />
            ))}
            {/* već u bazi (osim odmora i nepoznatog): osenčen širi luk ispod aktivnosti listića — samo prikaz, ne unosi se */}
            {postojece.map((s, i) => (
                <g key={`b${i}`} className="disc-baza">
                    <title>{`Već u bazi (${IZVOR_OPIS(s)}): ${activityLabel(s.type)} ${fmtMin(s.start)}–${s.end >= 1440 ? '24:00' : fmtMin(s.end)}`}</title>
                    <path d={arc(cx, cy, rBand, s.start, s.end)} fill="none" stroke={ACT_VAR[s.type] || ACT_VAR.unknown} strokeOpacity={0.45} strokeWidth={wBand * 1.9} />
                    {/* šrafura: kose tamne linije preko senke (isti ugao na celom krugu) */}
                    <path d={arc(cx, cy, rBand, s.start, s.end)} fill="none" stroke={`url(#disc-sraf-${clipId})`} strokeWidth={wBand * 1.9} />
                </g>
            ))}
            {akt.map((a, i) => (
                <path key={`a${i}`} d={arc(cx, cy, rBand, a.start, a.end)} fill="none" stroke={tipBoja(a.type)} strokeOpacity={i === izabran ? 0.85 : 0.55}
                    strokeWidth={i === izabran ? wBand * 1.35 : wBand}>
                    <title>{`${fmtMin(a.start)}–${a.end === 1440 ? '24:00' : fmtMin(a.end)}`}</title>
                </path>
            ))}
            {/* izabrana aktivnost: granice kao linije iz centra (kao kupa u pojedinačnoj obradi) */}
            {izabranA && [izabranA.start, izabranA.end].map((m) => {
                const [x, y] = pt(0.98 * R, m);
                return <line key={m} x1={cx} y1={cy} x2={x} y2={y} className="disc-handle" style={{ strokeWidth: 0.008 * R }} />;
            })}
            <line x1={cx + 0.2 * R * Math.cos(u)} y1={cy + 0.2 * R * Math.sin(u)} x2={cx + R * Math.cos(u)} y2={cy + R * Math.sin(u)}
                className="disc-insert" style={{ strokeWidth: 0.008 * R }} />
            {onUlaganje && (
                <circle cx={cx + 0.94 * R * Math.cos(u)} cy={cy + 0.94 * R * Math.sin(u)} r={0.024 * R} className="disc-insert-handle">
                    <title>Prevucite da pomerite početak aktivnosti</title>
                </circle>
            )}
            {/* UTC skala */}
            {utc && (
                <g className="disc-utc" style={{ fontSize: 0.05 * R }}>
                    {SATI.map((h) => {
                        const [x1, y1] = pt(1.0 * R, h * 60), [x2, y2] = pt(1.045 * R, h * 60), [tx, ty] = pt(1.1 * R, h * 60);
                        return (
                            <g key={h}>
                                <line x1={x1} y1={y1} x2={x2} y2={y2} style={{ strokeWidth: 0.006 * R }} />
                                <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central">{h}</text>
                            </g>
                        );
                    })}
                    <text x={cx - 0.86 * R} y={cy - 0.98 * R} textAnchor="middle" className="disc-utc-label">UTC</text>
                </g>
            )}
            </g>
            {/* lupa: isti crtež (use) uvećan LUPA_X puta oko miša, u gornjem levom uglu okvira (donjem levom kad je miš baš u tom uglu) */}
            {lupa && lupa.x != null && (() => {
                const L = 0.78 * M, w = L / LUPA_X, m = 0.02 * M;
                const v = lupa.v || { x0: cx - M, y0: cy - M, x1: cx + M, y1: cy + M };
                const lx = v.x0 + m, ly = lupa.x < lx + L * 1.05 && lupa.y < v.y0 + m + L * 1.05 ? v.y1 - m - L : v.y0 + m;
                return (
                    <g className="disc-lupa" pointerEvents="none">
                        <svg x={lx} y={ly} width={L} height={L} viewBox={`${lupa.x - w / 2} ${lupa.y - w / 2} ${w} ${w}`}>
                            <rect x={lupa.x - w} y={lupa.y - w} width={2 * w} height={2 * w} className="disc-lupa-pozadina" />
                            <use href={`#disc-sadrzaj-${clipId}`} />
                            <line x1={lupa.x - w / 2} y1={lupa.y} x2={lupa.x + w / 2} y2={lupa.y} className="disc-lupa-krst" style={{ strokeWidth: w / 250 }} />
                            <line x1={lupa.x} y1={lupa.y - w / 2} x2={lupa.x} y2={lupa.y + w / 2} className="disc-lupa-krst" style={{ strokeWidth: w / 250 }} />
                        </svg>
                        <rect x={lx} y={ly} width={L} height={L} rx={0.02 * M} className="disc-lupa-okvir" style={{ strokeWidth: 0.006 * M }} />
                    </g>
                );
            })()}
        </svg>
    );
}
